import * as faceapi from "face-api.js";


const MODEL_PATH = "/face-api-models";
let modelPromise = null;


const clamp = (value, min, max) => Math.min(max, Math.max(min, value));


const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error("Image could not be loaded for verification"));
  image.src = src;
});


export const loadFaceVerificationModels = async () => {
  if (!modelPromise) {
    modelPromise = Promise.all([
      faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_PATH),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_PATH),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_PATH),
    ]);
  }
  await modelPromise;
};


const detectFace = async (image) => faceapi
  .detectSingleFace(image, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.35 }))
  .withFaceLandmarks()
  .withFaceDescriptor();


export const compareFacePhotos = async ({ livePhotoData, profilePhotoData }) => {
  if (!livePhotoData || !profilePhotoData) {
    return {
      confidence: null,
      distance: null,
      match: false,
      summary: "A live photo and profile picture are both required for local verification.",
      concerns: ["Missing verification image"],
      status: "local_verification_unavailable",
    };
  }
  try {
    await loadFaceVerificationModels();
    const [liveImage, profileImage] = await Promise.all([loadImage(livePhotoData), loadImage(profilePhotoData)]);
    const [liveDetection, profileDetection] = await Promise.all([detectFace(liveImage), detectFace(profileImage)]);
    if (!liveDetection || !profileDetection) {
      return {
        confidence: 0,
        distance: null,
        match: false,
        summary: "A clear face could not be detected in one of the photos.",
        concerns: ["Face not detected clearly in one of the photos"],
        status: "face_not_detected",
      };
    }
    const distance = faceapi.euclideanDistance(liveDetection.descriptor, profileDetection.descriptor);
    const confidence = clamp(1 - (distance / 1.2), 0, 1);
    const match = confidence >= 0.52;
    return {
      confidence,
      distance,
      match,
      summary: confidence >= 0.78
        ? "Local TensorFlow verification found a strong facial match."
        : confidence >= 0.52
          ? "Local TensorFlow verification is uncertain and may need Gemini fallback."
          : "Local TensorFlow verification found a weak facial match.",
      concerns: confidence >= 0.52 ? [] : ["Low local face similarity score"],
      status: confidence >= 0.78 ? "approved" : confidence >= 0.52 ? "fallback_candidate" : "below_local_threshold",
    };
  } catch (error) {
    return {
      confidence: null,
      distance: null,
      match: false,
      summary: error.message || "Local TensorFlow verification did not complete.",
      concerns: [error.message || "Local verifier unavailable"],
      status: "local_verification_unavailable",
    };
  }
};